import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { PrayerPage } from "../components/PrayerPage";
import { prayerBySlug, prayerPath } from "../lib/prayers";
import {
  articleJsonLd,
  breadcrumbJsonLd,
  canonical,
  documentHeaders,
  faqJsonLd,
  prayerOgImage,
  socialMeta,
} from "../lib/seo";

/**
 * `/prayers/<situation>` — one written prayer.
 *
 * `BlogPosting`, the same as the collections, and deliberately not `HowTo`.
 * `HowTo` asserts a procedure with steps that produce a result, which is a
 * claim about prayer this site is not making and a rich-result grab dressed as
 * markup. The page is a piece of writing with a prayer in it.
 */
export const Route = createFileRoute("/prayers/$slug")({
  loader: ({ params }) => {
    const prayer = prayerBySlug(params.slug);
    if (!prayer) throw notFound();
    return prayer;
  },
  headers: documentHeaders,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const path = prayerPath(loaderData.slug);
    return {
      meta: [
        { title: loaderData.title },
        { name: "description", content: loaderData.description },
        ...socialMeta({
          title: loaderData.title,
          description: loaderData.description,
          path,
          lang: "en",
          image: prayerOgImage(loaderData.slug),
          imageAlt: loaderData.h1,
        }),
        {
          "script:ld+json": articleJsonLd({
            headline: loaderData.h1,
            description: loaderData.description,
            path,
            published: loaderData.published,
            updated: loaderData.updated,
          }),
        },
        {
          "script:ld+json": breadcrumbJsonLd([
            { name: "Bible Shield", path: "/" },
            { name: "Prayers", path: "/prayers" },
            { name: loaderData.h1, path },
          ]),
        },
        ...(loaderData.faq
          ? [{ "script:ld+json": faqJsonLd(loaderData.faq) }]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical(path) }],
    };
  },
  component: PrayerRoute,
});

function PrayerRoute() {
  const prayer = Route.useLoaderData();
  return (
    <PageChrome>
      <nav
        aria-label="breadcrumb"
        className="mx-auto mb-6 w-full max-w-[68ch] text-sm text-ink2"
      >
        <Link
          to="/prayers"
          className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
        >
          prayers
        </Link>
      </nav>
      <PrayerPage prayer={prayer} />
    </PageChrome>
  );
}
